export const DEFAULT_UNIT_ECONOMICS_INPUTS = {
  revenue: 350000,
  sales: 85,
  period: 45,
  cogs: 175000,
  marketing: 25000,
  tax: 21000,
  marginWant: 7,
  cr1: 5,
  cr2: 50,
  growth: 25,
};

export const UNIT_ECONOMICS_FORMULA_STEPS = [
  {
    marker: '①',
    title: 'Средний чек и прибыль на продажу',
    formula: 'Средний чек = Выручка ÷ Продажи · Прибыль на продажу = (Выручка − Все расходы) ÷ Продажи',
    example: 'Пример: 350 000 ÷ 85 = <b>4 118 ₸</b> чек. (350 000 − 221 000) ÷ 85 = <b>1 518 ₸</b> прибыли с каждой продажи',
  },
  {
    marker: '②',
    title: 'CPS — сколько можно потратить на привлечение одной продажи',
    formula: 'CPS = Средний чек × Желаемая маржинальность%',
    example: 'Пример: 4 118 × 7% = <b>288 ₸</b> — максимум на маркетинг чтобы остаться с нужной маржой',
  },
  {
    marker: '③',
    title: 'CPL — стоимость лида и CPC — стоимость клика',
    formula: 'CPL = CPS × CR2 · CPC = CPL × CR1',
    example: 'Пример: 288 × 50% = <b>144 ₸</b> за лид. 144 × 5% = <b>7 ₸</b> за клик — столько может стоить клик чтобы экономика сходилась',
  },
  {
    marker: '④',
    title: 'План на следующий месяц',
    formula: 'План продаж = (Продажи ÷ Период) × 30 × (1 + Рост%) · Бюджет = План × CPL ÷ CR2',
    example: 'Пример: (85 ÷ 45) × 30 × 1.25 = <b>71 продажа</b>. 71 × 144 ÷ 50% = <b>20 448 ₸</b> рекламного бюджета',
  },
];

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  const rounded = Math.round(value * 100) / 100;
  return `${rounded.toLocaleString(locale, { maximumFractionDigits: 2 })} ₸`;
};

export const formatPercent = (value, digits = 2) => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  const rounded = Math.round(value * 10 ** digits) / 10 ** digits;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(digits).replace(/\.?0+$/, '');

  return `${text}%`;
};

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return min;
  }

  return Math.min(max, Math.max(min, parsed));
};

export const calculateUnitEconomicsMetrics = (inputs) => {
  const revenue = clampNumber(inputs.revenue, 0);
  const sales = clampNumber(inputs.sales, 1);
  const period = clampNumber(inputs.period, 1);
  const cogs = clampNumber(inputs.cogs, 0);
  const marketing = clampNumber(inputs.marketing, 0);
  const tax = clampNumber(inputs.tax, 0);
  const marginWantPct = clampNumber(inputs.marginWant, 1, 99);
  const marginWant = marginWantPct / 100;
  const cr1 = clampNumber(inputs.cr1, 0.1, 100) / 100;
  const cr2 = clampNumber(inputs.cr2, 0.1, 100) / 100;
  const growth = clampNumber(inputs.growth, -50, 500) / 100;

  const avgCheck = revenue / sales;
  const perDay = sales / period;
  const totalCost = cogs + marketing + tax;
  const costPerSale = totalCost / sales;
  const profitTotal = revenue - totalCost;
  const profitPerSale = profitTotal / sales;
  const marginFact = revenue > 0 ? (profitTotal / revenue) * 100 : 0;

  const cps = avgCheck * marginWant;
  const cpl = cr2 > 0 ? cps * cr2 : 0;
  const cpc = cr1 > 0 ? cpl * cr1 : 0;

  const salesPerMonth = (sales / period) * 30;
  const planSales = Math.round(salesPerMonth * (1 + growth));
  const planBudget = cr2 > 0 ? planSales * (cpl / cr2) : 0;
  const planRevenue = planSales * avgCheck;
  const planProfit = revenue > 0 ? planRevenue * (profitTotal / revenue) : 0;

  return {
    avgCheck,
    perDay,
    totalCost,
    costPerSale,
    profitTotal,
    profitPerSale,
    marginFact,
    marginWantPct,
    cps,
    cpl,
    cpc,
    planSales,
    planBudget,
    planRevenue,
    planProfit,
    revenue,
  };
};

export const getInsightState = (metrics) => {
  const {
    profitTotal,
    totalCost,
    revenue,
    marginFact,
    marginWantPct,
    cpl,
    cpc,
  } = metrics;

  if (profitTotal < 0) {
    return {
      variant: 'bad',
      message: 'Бизнес убыточен при текущих параметрах: расходы <b>{totalCost}</b> превышают выручку <b>{revenue}</b>. Нужно снизить себестоимость или поднять цену.',
      vars: { totalCost, revenue },
    };
  }

  if (marginFact < 5) {
    return {
      variant: 'warn',
      message: 'Маржинальность <b>{marginFact}</b> — очень низкая. При таком уровне любой рост расходов или падение продаж переводит бизнес в минус. Проверьте себестоимость и цену.',
      vars: { marginFact },
    };
  }

  if (cpl < 100) {
    return {
      variant: 'warn',
      message: 'Допустимый CPL <b>{cpl}</b> — очень низкий. Найти такой трафик будет сложно. Попробуйте повысить желаемую маржинальность или конверсию CR2.',
      vars: { cpl },
    };
  }

  if (marginFact >= marginWantPct) {
    return {
      variant: 'good',
      message: 'Фактическая маржинальность <b>{marginFact}</b> выше цели <b>{marginWant}</b>. Можно смелее вкладывать в рекламу — допустимый CPL <b>{cpl}</b>, CPC <b>{cpc}</b>.',
      vars: { marginFact, marginWant: marginWantPct, cpl, cpc },
    };
  }

  const gapCost = totalCost - revenue * (1 - marginWantPct / 100);

  return {
    variant: 'warn',
    message: 'Маржинальность <b>{marginFact}</b> ниже желаемой <b>{marginWant}</b>. Чтобы достичь цели, нужно либо снизить расходы на <b>{gapCost}</b>, либо поднять выручку.',
    vars: { marginFact, marginWant: marginWantPct, gapCost },
  };
};
